import type { AnswerValue, IterationContext } from '../dto/survey/survey-answers.types';
import type { NodeId, SurveyNode, SurveySchema } from '../dto/survey/survey-schema.types';
import { indexAllNodesById } from './node-index';
import { isExitEnd, type NextResolution, resolveNext, resolveNextFromTarget } from './resolve-next';

/**
 * §3quater.3: the single walker every caller that needs "the path the survey
 * engine actually takes" now shares — submit (replacing the old
 * `walkSubmitPath`/`resolveSubmitPath` pair), the NC profile validator, and
 * the questionnaire PDF (replacing `answers.navigationHistory`, a client-
 * reported value that can omit or reorder nodes the client never actually
 * visited).
 */
export type PathStep = {
  nodeId: NodeId;
  answerKey: string;
  iterationContext: IterationContext | null;
  answered: boolean;
};

export type PathStop =
  | { kind: 'end'; endNodeId: NodeId | null }
  | { kind: 'exit'; endNodeId: NodeId }
  | {
      kind: 'awaiting';
      nodeId: NodeId;
      answerKey: string;
      iterationContext: IterationContext | null;
    }
  | { kind: 'boundary'; nodeId: NodeId }
  | { kind: 'aborted'; reason: 'missing-node' | 'iteration-cap'; nodeId: NodeId };

export interface WalkOptions {
  startNodeId?: NodeId;
  settledKeys?: ReadonlySet<string>;
  mustAnswer?: (node: SurveyNode) => boolean;
  stopAt?: (node: SurveyNode) => boolean;
  maxSteps?: number;
}

/** Matches the backend's former `SUBMIT_WALK_ITERATION_CAP` — a DoS guard on a
 * malicious/malformed `from-answer` repeat-block count, not a real limit any
 * legitimate survey approaches. */
export const DEFAULT_WALK_ITERATION_CAP = 10_000;

const EMPTY_SETTLED_KEYS: ReadonlySet<string> = new Set();

function answerKeyFor(nodeId: NodeId, iterCtx: IterationContext | null): string {
  return iterCtx ? `${nodeId}#${iterCtx.index}` : nodeId;
}

type Advance = NextResolution | { kind: 'awaiting' };

/**
 * Where an UNANSWERED node advances to, when it is allowed to be unanswered
 * (rule 5, §3quater.3): a `linear` next is followed as usual, a
 * `jump-by-option` next falls back to its own `default` (deterministic —
 * that is exactly what `default` is for), and a `jump-by-boolean` next has
 * no default to fall back on, so the walk cannot proceed without a real
 * answer. Still runs the target through `resolveNextFromTarget` so a
 * repeat-block boundary/`enterWhen` guard on the resolved target behaves
 * identically to an answered node — one derivation, not a parallel copy of
 * that plumbing.
 */
function advanceUnanswered(
  node: Exclude<SurveyNode, { type: 'end' }>,
  schema: SurveySchema,
  iterCtx: IterationContext | null,
  answers: { byNode: Record<string, AnswerValue> },
): Advance {
  const next = node.next;
  if (!next) {
    return { kind: 'end-of-survey' };
  }
  if (next.kind === 'jump-by-boolean') {
    return { kind: 'awaiting' };
  }
  const rawTarget = next.kind === 'linear' ? next.nextNodeId : next.default;
  return resolveNextFromTarget(rawTarget, schema, iterCtx, answers);
}

type StepOutcome = { advance: Advance } | { stop: PathStop };

/**
 * Rules 3-5 of §3quater.3, for a single non-`end` node: decides whether the
 * node is answered/settled/optional (and so contributes a `PathStep` and an
 * `Advance` to compute next) or genuinely blocks the walk (`stop`). Kept
 * outside the loop body to stay under the cognitive-complexity budget.
 *
 * `rawAnswers` is the caller's raw input, read ONLY to check whether THIS
 * node has an answer. `pathAnswers` is the accumulator of every answer for a
 * node the walk has actually stepped through so far — it is what gets
 * handed to `resolveNext`/`advanceUnanswered` for computing where to go
 * next, so an `enterWhen` guard (or a repeat-block's `from-answer` count)
 * can only ever be satisfied by a node genuinely on the path. A stale or
 * forged entry in `rawAnswers` for a node the walk never actually reaches
 * must not leak into that decision (§3quater item 2) — a submitted payload
 * is not a trusted description of what was actually visited.
 */
function computeStepOutcome(
  node: Exclude<SurveyNode, { type: 'end' }>,
  answerKey: string,
  iterCtx: IterationContext | null,
  schema: SurveySchema,
  rawAnswers: { byNode: Record<string, AnswerValue> },
  pathAnswers: Record<string, AnswerValue>,
  mustAnswer: (node: SurveyNode) => boolean,
  settledKeys: ReadonlySet<string>,
  steps: PathStep[],
): StepOutcome {
  if (node.type === 'info-screen') {
    steps.push({ nodeId: node.id, answerKey, iterationContext: iterCtx, answered: false });
    return { advance: advanceUnanswered(node, schema, iterCtx, { byNode: pathAnswers }) };
  }

  const answer = rawAnswers.byNode[answerKey];
  if (answer !== undefined) {
    steps.push({ nodeId: node.id, answerKey, iterationContext: iterCtx, answered: true });
    pathAnswers[answerKey] = answer;
    return { advance: resolveNext(node, answer, schema, iterCtx, { byNode: pathAnswers }) };
  }

  if (settledKeys.has(answerKey) || !mustAnswer(node)) {
    steps.push({ nodeId: node.id, answerKey, iterationContext: iterCtx, answered: false });
    return { advance: advanceUnanswered(node, schema, iterCtx, { byNode: pathAnswers }) };
  }

  return { stop: { kind: 'awaiting', nodeId: node.id, answerKey, iterationContext: iterCtx } };
}

type CurrentNode =
  | { kind: 'node'; node: Exclude<SurveyNode, { type: 'end' }> }
  | { kind: 'stop'; stop: PathStop };

/** Rules 1-2 of §3quater.3, plus the iteration cap: everything that can end
 * the walk BEFORE a node is treated as an answerable/info step. */
function resolveCurrentNode(
  nodeId: NodeId,
  visitedCount: number,
  maxSteps: number,
  byId: Map<NodeId, SurveyNode>,
  stopAt: ((node: SurveyNode) => boolean) | undefined,
): CurrentNode {
  const node = byId.get(nodeId);
  if (!node) {
    return { kind: 'stop', stop: { kind: 'aborted', reason: 'missing-node', nodeId } };
  }
  if (stopAt?.(node)) {
    return { kind: 'stop', stop: { kind: 'boundary', nodeId } };
  }
  if (node.type === 'end') {
    const endNodeId = node.id;
    return {
      kind: 'stop',
      stop: isExitEnd(node) ? { kind: 'exit', endNodeId } : { kind: 'end', endNodeId },
    };
  }
  if (visitedCount > maxSteps) {
    return { kind: 'stop', stop: { kind: 'aborted', reason: 'iteration-cap', nodeId } };
  }
  return { kind: 'node', node };
}

export function walkSurveyPath(
  schema: SurveySchema,
  answers: { byNode: Record<string, AnswerValue> },
  options: WalkOptions = {},
): { steps: PathStep[]; stop: PathStop } {
  const byId = indexAllNodesById(schema.nodes);
  const mustAnswer = options.mustAnswer ?? ((node: SurveyNode) => node.required);
  const settledKeys = options.settledKeys ?? EMPTY_SETTLED_KEYS;
  const maxSteps = options.maxSteps ?? DEFAULT_WALK_ITERATION_CAP;

  const steps: PathStep[] = [];
  // Answers of nodes the walk has actually stepped through so far — see
  // `computeStepOutcome`'s doc comment (§3quater item 2).
  const pathAnswers: Record<string, AnswerValue> = {};
  let nodeId: NodeId = options.startNodeId ?? schema.startNodeId;
  let iterCtx: IterationContext | null = null;
  let visitedCount = 0;

  const applyAdvance = (advance: Advance): PathStop | null => {
    if (advance.kind === 'awaiting') {
      return {
        kind: 'awaiting',
        nodeId,
        answerKey: answerKeyFor(nodeId, iterCtx),
        iterationContext: iterCtx,
      };
    }
    if (advance.kind === 'end-of-survey') {
      return { kind: 'end', endNodeId: null };
    }
    nodeId = advance.nodeId;
    iterCtx = advance.iterationContext ?? null;
    return null;
  };

  while (true) {
    visitedCount += 1;
    const current = resolveCurrentNode(nodeId, visitedCount, maxSteps, byId, options.stopAt);
    if (current.kind === 'stop') {
      return { steps, stop: current.stop };
    }
    const node = current.node;

    const answerKey = answerKeyFor(node.id, iterCtx);
    const outcome = computeStepOutcome(
      node,
      answerKey,
      iterCtx,
      schema,
      answers,
      pathAnswers,
      mustAnswer,
      settledKeys,
      steps,
    );
    if ('stop' in outcome) {
      return { steps, stop: outcome.stop };
    }
    const stop = applyAdvance(outcome.advance);
    if (stop) {
      return { steps, stop };
    }
  }
}

function staticEdgeTargets(node: SurveyNode): NodeId[] {
  if (node.type === 'end') {
    return [];
  }
  const next = node.next;
  if (!next) {
    return [];
  }
  if (next.kind === 'linear') {
    return next.nextNodeId === 'END' ? [] : [next.nextNodeId];
  }
  if (next.kind === 'jump-by-option') {
    return [next.default, ...Object.values(next.perOption)].filter((id) => id !== 'END');
  }
  return [next.whenTrue, next.whenFalse].filter((id) => id !== 'END');
}

/**
 * Plain multi-source reachability over the schema's static edges, ignoring
 * answers entirely (every branch counts, same over-approximation spirit as
 * `collectReachableNodeIds`) — used by graph invariants that need "can node
 * Y ever follow node X", not "does it follow it for THIS answer set". A
 * repeat-block's inner sequence is entered via its first inner node and
 * exited via the block's own `next`, matching how `resolveNext` walks it.
 */
export function reachableFrom(schema: SurveySchema, fromNodeIds: readonly NodeId[]): Set<NodeId> {
  const byId = new Map(schema.nodes.map((n) => [n.id, n] as const));
  const reached = new Set<NodeId>();
  const queue: NodeId[] = [...fromNodeIds];

  while (queue.length > 0) {
    const id = queue.shift();
    if (id === undefined || reached.has(id)) {
      continue;
    }
    const node = byId.get(id);
    if (!node) {
      continue;
    }
    reached.add(id);
    if (node.type === 'repeat-block') {
      // Every inner node is reachable once the block is entered at all — its
      // own internal `END` sentinel means "next iteration or block exit",
      // never "end of survey" (mirrors `resolveInsideBlock`), so it is
      // resolved separately below rather than through `staticEdgeTargets`.
      for (const innerNode of node.innerNodes) {
        reached.add(innerNode.id);
        queue.push(...staticEdgeTargets(innerNode));
      }
      if (node.next?.kind === 'linear' && node.next.nextNodeId !== 'END') {
        queue.push(node.next.nextNodeId);
      }
      continue;
    }
    queue.push(...staticEdgeTargets(node));
  }

  return reached;
}
